/* =============================================================================
   Armada – Recommended Products Carousel
   Custom element controlling the tabs, scroll-track and dots/scroll-bar
   progress. Card height and price-row alignment are CSS-driven (carousel
   overrides in assets/product-tile.css) — no JS measurement needed.
   Loaded with `defer`, so the constructor is registered after the document is
   fully parsed and every section instance can find its children synchronously
   in connectedCallback.
   ============================================================================= */

if (!customElements.get('armada-recommended-products')) {
  class ArmadaRecommendedProducts extends HTMLElement {
    connectedCallback() {
      this.tabs = this.querySelectorAll('.armada-recommended-products__tab');
      this.panels = this.querySelectorAll('.armada-recommended-products__panel');

      // AAS-4787: opt this instance into the staggered reveal. The CSS only hides
      // cards under [data-reveal], so if this script never runs the cards render
      // normally — the animation is a progressive enhancement, not a dependency.
      this.dataset.reveal = '';
      this.revealPanel(this.querySelector('.armada-recommended-products__panel.is-active'));

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const index = parseInt(tab.dataset.tabIndex, 10);
          this.switchTab(index);
        });
      });

      this.panels.forEach((panel) => {
        const track = panel.querySelector('[data-track]');
        const dotsContainer = panel.querySelector('[data-dots]');
        const scrollTrackEl = panel.querySelector('.armada-recommended-products__scroll-track');
        const trackFill = panel.querySelector('[data-track-fill]');

        if (!track || !dotsContainer) return;

        this.buildDots(track, dotsContainer);
        this.updateScrollTrack(track, scrollTrackEl, trackFill);

        track.addEventListener('scroll', () => {
          this.updateActiveDot(track, dotsContainer);
          this.updateScrollTrack(track, scrollTrackEl, trackFill);
        }, { passive: true });

        let resizeTimer;
        window.addEventListener('resize', () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => {
            this.buildDots(track, dotsContainer);
            this.updateScrollTrack(track, scrollTrackEl, trackFill);
          }, 200);
        }, { passive: true });

        // Search & Discovery tabs render skeletons server-side; fetch the real
        // recommendations for this panel's intent and swap them in.
        if (track.dataset.recUrl) {
          this.loadRecommendations(panel, track, dotsContainer, scrollTrackEl, trackFill);
        }
      });
    }

    loadRecommendations(panel, track, dotsContainer, scrollTrackEl, trackFill) {
      const base = track.dataset.recUrl;
      const index = panel.dataset.panelIndex;
      track.setAttribute('aria-busy', 'true');

      // Complementary first; fall back to related when the product has no
      // complementary items configured in Search & Discovery.
      this.fetchIntent(base, 'complementary', index)
        .then((markup) => (this.hasCards(markup) ? markup : this.fetchIntent(base, 'related', index)))
        .then((markup) => {
          track.removeAttribute('aria-busy');
          // No complementary and no related items → hide the whole tab.
          if (!this.hasCards(markup)) {
            this.hidePanel(panel);
            return;
          }
          track.innerHTML = markup;
          this.forceRepaint(track);
          this.buildDots(track, dotsContainer);
          this.updateScrollTrack(track, scrollTrackEl, trackFill);
        })
        .catch(() => {
          track.removeAttribute('aria-busy');
          this.hidePanel(panel);
        });
    }

    // Remove a tab + panel from view (e.g. a Search & Discovery tab that
    // resolved to zero products). Tabs/panels map by positional index, so we
    // hide rather than remove and keep that mapping intact; then re-activate a
    // surviving tab and collapse the tab bar / section as needed.
    hidePanel(panel) {
      const index = parseInt(panel.dataset.panelIndex, 10);
      const wasActive = panel.classList.contains('is-active');
      const tab = Array.from(this.tabs).find(
        (t) => parseInt(t.dataset.tabIndex, 10) === index
      );

      panel.classList.remove('is-active');
      panel.setAttribute('aria-hidden', 'true');
      panel.style.display = 'none';
      if (tab) {
        tab.style.display = 'none';
        tab.setAttribute('aria-hidden', 'true');
      }

      const visibleTabs = Array.from(this.tabs).filter((t) => t.style.display !== 'none');

      // If we just hid the active tab, activate the first surviving one.
      if (wasActive && visibleTabs.length) {
        this.switchTab(parseInt(visibleTabs[0].dataset.tabIndex, 10));
      }

      // A lone remaining tab needs no tab bar; nothing left hides the section.
      const tabBar = this.querySelector('.armada-recommended-products__tabs');
      if (tabBar && visibleTabs.length <= 1) {
        tabBar.style.display = 'none';
      }
      if (!visibleTabs.length) {
        this.style.display = 'none';
      }
    }

    // Injected cards carry absolutely-positioned, opacity-transitioned images
    // (hover-swap mode). On some browsers those stay unpainted until the next
    // scroll/reflow, showing a blank tile. Toggling display forces a synchronous
    // reflow + repaint of the subtree so the images show immediately.
    forceRepaint(el) {
      const previous = el.style.display;
      el.style.display = 'none';
      void el.offsetHeight;
      el.style.display = previous;
    }

    fetchIntent(base, intent, index) {
      const url = `${base}${base.includes('?') ? '&' : '?'}intent=${intent}`;

      return fetch(url)
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          // All panels share the section id, so the response re-renders every
          // panel — pick out only the one matching this panel's index.
          const freshTrack = doc.querySelector(
            `[data-panel-index="${index}"] .armada-recommended-products__track`
          );
          return freshTrack ? freshTrack.innerHTML : '';
        });
    }

    hasCards(markup) {
      if (!markup) return false;
      const scratch = document.createElement('div');
      scratch.innerHTML = markup;
      return scratch.querySelector('.product-tile-slot') !== null;
    }

    switchTab(index) {
      this.tabs.forEach((tab, i) => {
        tab.classList.toggle('is-active', i === index);
        tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
      });

      this.panels.forEach((panel, i) => {
        panel.classList.toggle('is-active', i === index);
        panel.setAttribute('aria-hidden', i === index ? 'false' : 'true');

        if (i !== index) return;

        const track = panel.querySelector('[data-track]');
        const dotsContainer = panel.querySelector('[data-dots]');
        const scrollTrackEl = panel.querySelector('.armada-recommended-products__scroll-track');
        const trackFill = panel.querySelector('[data-track-fill]');

        if (track && dotsContainer) {
          this.buildDots(track, dotsContainer);
          this.updateScrollTrack(track, scrollTrackEl, trackFill);
        }

        this.revealPanel(panel);
      });
    }

    /**
     * AAS-4787: stagger the incoming panel's cards in, but only once its images
     * have settled — the cards are lazily loaded and a hidden panel doesn't fetch
     * them, so revealing immediately made them pop in one by one.
     */
    revealPanel(panel) {
      if (!panel) return;

      const slots = panel.querySelectorAll('.product-tile-slot');
      if (!slots.length) return;

      // Restart the animation from the top on every switch.
      panel.classList.remove('is-revealed');
      slots.forEach((slot, i) => slot.style.setProperty('--reveal-order', i));

      this.whenImagesSettled(panel).then(() => {
        // Only reveal if this panel is still the active one — a fast tab switch
        // while images were loading must not flash a stale panel back in.
        if (!panel.classList.contains('is-active')) return;
        requestAnimationFrame(() => panel.classList.add('is-revealed'));
      });
    }

    /**
     * Resolves once every image in the panel has loaded (or failed), with a cap so
     * a slow or broken image can never hold the reveal back indefinitely.
     */
    whenImagesSettled(panel) {
      const pending = [...panel.querySelectorAll('img')].filter((img) => !img.complete);
      if (!pending.length) return Promise.resolve();

      const loaded = Promise.all(
        pending.map(
          (img) =>
            new Promise((resolve) => {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            })
        )
      );

      return Promise.race([loaded, new Promise((resolve) => setTimeout(resolve, 1200))]);
    }

    buildDots(track, dotsContainer) {
      const dotsWrap = dotsContainer.closest('.armada-recommended-products__dots-wrap');
      const cards = track.querySelectorAll('.product-tile');
      if (!cards.length || !dotsWrap) return;

      requestAnimationFrame(() => {
        const trackWidth = track.clientWidth;
        const cardWidth = cards[0].offsetWidth;
        if (!cardWidth || !trackWidth) return;

        const visibleCount = Math.max(1, Math.floor(trackWidth / cardWidth));
        const pageCount = Math.ceil(cards.length / visibleCount);

        if (pageCount <= 1) {
          dotsContainer.innerHTML = '';
          dotsWrap.setAttribute('hidden', '');
          dotsWrap.setAttribute('aria-hidden', 'true');
          return;
        }

        dotsWrap.removeAttribute('hidden');
        dotsWrap.removeAttribute('aria-hidden');

        if (dotsContainer.children.length === pageCount) {
          this.updateActiveDot(track, dotsContainer);
          return;
        }

        dotsContainer.innerHTML = '';

        for (let i = 0; i < pageCount; i += 1) {
          const dot = document.createElement('button');
          dot.className = `armada-recommended-products__dot${i === 0 ? ' is-active' : ''}`;
          dot.setAttribute('type', 'button');
          dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
          dot.dataset.dotIndex = i;

          dot.addEventListener('click', () => {
            const scrollTarget = i * visibleCount * cardWidth;
            track.scrollTo({ left: scrollTarget, behavior: 'smooth' });
          });

          dotsContainer.appendChild(dot);
        }
      });
    }

    updateActiveDot(track, dotsContainer) {
      const dots = dotsContainer.querySelectorAll('.armada-recommended-products__dot');
      if (!dots.length) return;

      const cards = track.querySelectorAll('.product-tile');
      if (!cards.length) return;

      const cardWidth = cards[0].offsetWidth;
      const trackWidth = track.clientWidth;
      if (!cardWidth || !trackWidth) return;

      const visibleCount = Math.max(1, Math.floor(trackWidth / cardWidth));
      const pageIndex = Math.round(track.scrollLeft / (visibleCount * cardWidth));

      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === pageIndex);
      });
    }

    updateScrollTrack(track, scrollTrackEl, trackFill) {
      if (!track || !scrollTrackEl || !trackFill) return;

      const maxScroll = track.scrollWidth - track.clientWidth;

      if (maxScroll <= 0) {
        scrollTrackEl.setAttribute('hidden', '');
        scrollTrackEl.setAttribute('aria-hidden', 'true');
        trackFill.style.transform = 'translateX(0%)';
        return;
      }

      scrollTrackEl.removeAttribute('hidden');
      scrollTrackEl.removeAttribute('aria-hidden');

      const progress = track.scrollLeft / maxScroll;
      trackFill.style.transform = `translateX(${progress * 100}%)`;
    }
  }

  customElements.define('armada-recommended-products', ArmadaRecommendedProducts);
}
